// ============================================================
// Chess Arena — Check Detector
// ============================================================
// Determines which cells a piece attacks, using the live Board.
// Used by PieceManager to show check indicators on kings.
// ============================================================

import { GridPos, PieceState, BOARD_COLS, BOARD_ROWS } from '../core/types';
import { Board } from '../core/board';

function isValid(pos: GridPos): boolean {
  return pos.col >= 0 && pos.col < BOARD_COLS && pos.row >= 0 && pos.row < BOARD_ROWS;
}

/** Get all cells a piece threatens (attack squares, not valid moves). */
export function getAttackedCellsForPiece(piece: PieceState, board: Board): GridPos[] {
  const cells: GridPos[] = [];
  const { col, row } = piece.pos;

  switch (piece.type) {
    case 'Pawn': {
      // Pawns attack diagonally
      for (const dc of [-1, 1]) {
        for (const dr of [-1, 1]) {
          const t = { col: col + dc, row: row + dr };
          if (isValid(t) && board.isPassable(t)) cells.push(t);
        }
      }
      break;
    }
    case 'Knight': {
      const offsets = [
        [-2, -1], [-2, 1], [2, -1], [2, 1],
        [-1, -2], [1, -2], [-1, 2], [1, 2],
      ];
      for (const [dc, dr] of offsets) {
        const t = { col: col + dc, row: row + dr };
        if (isValid(t) && board.isPassable(t)) cells.push(t);
      }
      break;
    }
    case 'King': {
      for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
          if (dc === 0 && dr === 0) continue;
          const t = { col: col + dc, row: row + dr };
          if (isValid(t) && board.isPassable(t)) cells.push(t);
        }
      }
      break;
    }
    case 'Bishop':
      addSliding(cells, piece.pos, [[-1,-1],[1,-1],[-1,1],[1,1]], board);
      break;
    case 'Rook':
      addSliding(cells, piece.pos, [[0,-1],[0,1],[-1,0],[1,0]], board);
      break;
    case 'Queen':
      addSliding(cells, piece.pos, [
        [-1,-1],[1,-1],[-1,1],[1,1],[0,-1],[0,1],[-1,0],[1,0],
      ], board);
      break;
  }
  return cells;
}

function addSliding(
  cells: GridPos[],
  origin: GridPos,
  dirs: number[][],
  board: Board,
): void {
  for (const [dc, dr] of dirs) {
    let c = origin.col + dc;
    let r = origin.row + dr;
    let steps = 0;
    while (isValid({ col: c, row: r }) && steps < 4) {
      const pos = { col: c, row: r };
      if (!board.isPassable(pos)) break;
      cells.push(pos);
      // Stop after hitting any piece (but that cell is still attacked)
      const cell = board.getCellContent(pos);
      if (cell && cell.pieceId) break;
      c += dc;
      r += dr;
      steps++;
    }
  }
}
